import { useState, useEffect, useCallback, lazy, Suspense, useRef } from "react";
import "@/App.css";
import axios from "axios";

// Accessibility testing (development only)
import { initAccessibilityTesting } from "@/utils/accessibility";

// Layout components - always loaded
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { AccessibilityToolbar } from "@/components/ui/AccessibilityToolbar";

// Page components - lazy loaded for faster initial load
const HomePage = lazy(() => import("@/components/pages/HomePage").then(m => ({ default: m.HomePage })));
const ContactPage = lazy(() => import("@/components/pages/ContactPage").then(m => ({ default: m.ContactPage })));
const AboutPage = lazy(() => import("@/components/pages/AboutPage").then(m => ({ default: m.AboutPage })));
const InsightsPage = lazy(() => import("@/components/pages/InsightsPage").then(m => ({ default: m.InsightsPage })));
const AdminPage = lazy(() => import("@/components/pages/AdminPage").then(m => ({ default: m.AdminPage })));
const SolarPage = lazy(() => import("@/components/pages/SolarPage").then(m => ({ default: m.SolarPage })));
const WindPage = lazy(() => import("@/components/pages/WindPage").then(m => ({ default: m.WindPage })));
const MarinePage = lazy(() => import("@/components/pages/MarinePage").then(m => ({ default: m.MarinePage })));
const PublicInfraPage = lazy(() => import("@/components/pages/PublicInfraPage").then(m => ({ default: m.PublicInfraPage })));
const PrivacyPolicyPage = lazy(() => import("@/components/pages/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import("@/components/pages/TermsPage").then(m => ({ default: m.TermsPage })));
const AccessibilityPage = lazy(() => import("@/components/pages/AccessibilityPage").then(m => ({ default: m.AccessibilityPage })));

// Chat components - lazy loaded
const ChatWidget = lazy(() => import("@/components/chat/ChatWidget").then(m => ({ default: m.ChatWidget })));
const EnquiryFormModal = lazy(() => import("@/components/chat/EnquiryFormModal").then(m => ({ default: m.EnquiryFormModal })));

// Custom hooks
import { useChat } from "@/hooks/useChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading Screen Component with Logo Animation
// Actual George Energy Logo base64 for instant loading (FULL LOGO with sun and wave)
const LOGO_BASE64 = "data:image/png;base64,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";

const LoadingScreen = ({ isLoading }) => {
  if (!isLoading) return null;
  
  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 flex items-center justify-center transition-opacity duration-500">
      <div className="text-center">
        {/* Animated Logo */}
        <div className="relative">
          <div className="w-36 h-36 sm:w-44 sm:h-44 mx-auto mb-6 relative">
            {/* Glow ring */}
            <div className="absolute inset-0 rounded-full bg-lime-400/20 animate-ping" style={{ animationDuration: '2s' }} />
            {/* Logo container - generous padding to ensure logo fits completely */}
            <div className="relative w-full h-full rounded-full bg-white flex items-center justify-center shadow-2xl shadow-lime-500/30 overflow-hidden p-6">
              <img 
                src="/george_energy_logo_optimized.png" 
                alt="George Energy" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          {/* Brand text */}
          <h1 className="text-white text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            George Energy
          </h1>
          <p className="text-lime-400 text-sm sm:text-base italic">Energy Thought Through</p>
        </div>
        {/* Loading bar */}
        <div className="mt-8 w-48 sm:w-64 h-1 bg-emerald-800 rounded-full overflow-hidden mx-auto">
          <div className="h-full bg-gradient-to-r from-lime-400 to-emerald-400 rounded-full animate-loading-bar" />
        </div>
      </div>
    </div>
  );
};

// Scroll Progress Bar Component
const ScrollProgressBar = ({ progress }) => (
  <div className="fixed top-0 left-0 right-0 h-1 z-[60] bg-transparent">
    <div 
      className="h-full bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-500 transition-all duration-150 ease-out shadow-lg shadow-lime-400/50"
      style={{ width: `${progress}%` }}
    />
  </div>
);

// Loading spinner component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  
  // Helper function to get page from URL path
  const getPageFromPath = (path) => {
    const pageMap = {
      '/': 'home',
      '/about': 'about',
      '/contact': 'contact',
      '/insights': 'insights',
      '/solar': 'solar',
      '/wind': 'wind',
      '/marine': 'marine',
      '/public-infra': 'public-infra',
      '/public-infrastructure': 'public-infra',
      '/team': 'about',
      '/privacy': 'privacy',
      '/terms': 'terms',
      '/accessibility': 'accessibility'
    };
    return pageMap[path] || 'home';
  };

  // Page state - always start with 'home' on initial load
  // Navigation will happen through user interactions only
  const [currentPage, setCurrentPage] = useState('home');
  
  // Page announcement for screen readers (WCAG 4.1.3)
  const [pageAnnouncement, setPageAnnouncement] = useState('');
  
  // WCAG 2.4.2: Dynamic page titles
  const PAGE_TITLES = {
    'home': 'George Energy | Renewable Energy Development & Infrastructure',
    'about': 'About Us | George Energy',
    'contact': 'Contact | George Energy',
    'insights': 'Insights | George Energy',
    'solar': 'Solar Infrastructure | George Energy',
    'wind': 'Wind Infrastructure | George Energy',
    'marine': 'Marine Energy | George Energy',
    'public-infra': 'Public Infrastructure | George Energy',
    'privacy': 'Privacy Policy | George Energy',
    'terms': 'Terms of Service | George Energy',
    'accessibility': 'Accessibility Statement | George Energy',
    'admin': 'Admin | George Energy'
  };
  
  // Ref for main content focus management (WCAG 2.4.3)
  const mainContentRef = useRef(null);
  
  // Pending scroll target for page navigation (e.g., scroll to #vision on About page)
  const [pendingScrollTarget, setPendingScrollTarget] = useState(null);

  // Initial loading - wait for loading bar animation to complete
  // Respect the current URL path on initial load
  useEffect(() => {
    // Preload the video so it's ready when loading ends
    const video = document.createElement('video');
    video.src = '/hero_video_sharp_enhanced.mp4';
    video.preload = 'auto';
    video.muted = true;
    video.load();
    
    // Map URL path to page name
    const pageMap = {
      '/': 'home',
      '/about': 'about',
      '/contact': 'contact',
      '/insights': 'insights',
      '/solar': 'solar',
      '/wind': 'wind',
      '/marine': 'marine',
      '/public-infra': 'public-infra',
      '/public-infrastructure': 'public-infra',
      '/team': 'about',
      '/privacy': 'privacy',
      '/terms': 'terms',
      '/accessibility': 'accessibility',
      '/calculator': 'calculator'
    };
    
    // Wait for loading bar to complete, then navigate to the correct page based on URL
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
      
      // Get the current path and navigate to the appropriate page
      const currentPath = window.location.pathname;
      const hash = window.location.hash;
      
      // Handle admin hash
      if (hash === '#admin' || hash === '#/admin') {
        setCurrentPage('admin');
        return;
      }
      
      // Set page based on current URL path (respecting direct links)
      const targetPage = pageMap[currentPath] || 'home';
      setCurrentPage(targetPage);
    }, 2500);
    
    return () => {
      clearTimeout(loadingTimer);
    };
  }, []);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector('.scrollbar-hide');
      if (scrollContainer) {
        const scrollTop = scrollContainer.scrollTop;
        const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        setScrollProgress(progress);
      }
    };
    
    // Add scroll listener to the scroll container
    const scrollContainer = document.querySelector('.scrollbar-hide');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [currentPage]);

  // Update URL when navigating - with page transition (SEO-friendly URLs)
  const handleSetCurrentPage = useCallback((page, scrollTo = null) => {
    // Human-readable page names for screen reader announcements (WCAG 4.1.3)
    const pageNames = {
      'home': 'Home',
      'about': 'About George Energy',
      'contact': 'Contact',
      'insights': 'Insights',
      'solar': 'Solar Infrastructure',
      'wind': 'Wind Infrastructure',
      'marine': 'Marine Energy',
      'public-infra': 'Public Infrastructure',
      'privacy': 'Privacy Policy',
      'terms': 'Terms of Service',
      'accessibility': 'Accessibility Statement',
      'admin': 'Admin'
    };
    
    // Update browser URL for SEO (clean URLs without hash)
    const urlMap = {
      'home': '/',
      'about': '/about',
      'contact': '/contact',
      'insights': '/insights',
      'solar': '/solar',
      'wind': '/wind',
      'marine': '/marine',
      'public-infra': '/public-infra',
      'privacy': '/privacy',
      'terms': '/terms',
      'accessibility': '/accessibility',
      'admin': '/#admin'
    };
    
    const newUrl = urlMap[page] || '/';
    window.history.pushState({ page, scrollTo }, '', newUrl);
    
    // Page transition effect
    setIsPageTransitioning(true);
    
    setTimeout(() => {
      // Dispatch reset scroll events for the target page (only if no scrollTo target)
      if (page === 'home') {
        window.dispatchEvent(new CustomEvent('resetHomeScroll'));
      } else if (page === 'about' && !scrollTo) {
        window.dispatchEvent(new CustomEvent('resetAboutScroll'));
      }
      
      // Set page and scroll target together so they're in sync
      setCurrentPage(page);
      setScrollProgress(0);
      
      // WCAG 4.1.3: Announce page change to screen readers
      setPageAnnouncement(`Navigated to ${pageNames[page] || page} page`);
      
      // WCAG 2.4.2: Update document title dynamically
      document.title = PAGE_TITLES[page] || PAGE_TITLES['home'];
      
      // Set pending scroll target AFTER page change to ensure component receives it
      if (scrollTo) {
        // Use a microtask to ensure this happens after the page state update
        setTimeout(() => {
          setPendingScrollTarget(scrollTo);
        }, 50);
      }
      
      // Instant scroll to top (for window)
      window.scrollTo(0, 0);
      
      // WCAG 2.4.3: Move focus to main content after navigation
      setTimeout(() => {
        if (mainContentRef.current) {
          mainContentRef.current.focus();
        }
      }, 100);
      
      setTimeout(() => {
        setIsPageTransitioning(false);
      }, 300);
    }, 200);
  }, []);
  
  // Initialize accessibility testing in development mode
  useEffect(() => {
    initAccessibilityTesting();
  }, []);

  // Listen for hash changes to detect admin URL access and handle browser back/forward
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#admin' || hash === '#/admin') {
        setCurrentPage('admin');
      }
    };
    
    // Handle browser back/forward buttons (popstate)
    const handlePopState = (e) => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      
      if (hash === '#admin' || hash === '#/admin') {
        setCurrentPage('admin');
        return;
      }
      
      // Map path to page
      const pageMap = {
        '/': 'home',
        '/about': 'about',
        '/contact': 'contact',
        '/insights': 'insights',
        '/solar': 'solar',
        '/wind': 'wind',
        '/marine': 'marine',
        '/public-infra': 'public-infra',
        '/public-infrastructure': 'public-infra',
        '/team': 'about',
        '/privacy': 'privacy',
        '/terms': 'terms',
        '/accessibility': 'accessibility'
      };
      
      const page = pageMap[path] || 'home';
      setCurrentPage(page);
      
      // Handle scroll target from state
      if (e.state && e.state.scrollTo) {
        setTimeout(() => {
          const element = document.getElementById(e.state.scrollTo);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    };
    
    // Listen for custom navigation events from HomePage
    const handleNavigate = (e) => {
      // Handle both old format (string) and new format (object with page and scrollTo)
      if (typeof e.detail === 'string') {
        handleSetCurrentPage(e.detail);
      } else if (e.detail && e.detail.page) {
        handleSetCurrentPage(e.detail.page, e.detail.scrollTo);
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('navigate', handleNavigate);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('navigate', handleNavigate);
    };
  }, [handleSetCurrentPage]);
  
  // Enquiry form state
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    service: 'solar',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Chat hook
  const chat = useChat();

  // Handle enquiry form submission
  const handleEnquirySubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/enquiry`, enquiryData);
      
      // Close form and open chat with confirmation
      setShowEnquiryForm(false);
      chat.setIsOpen(true);

      // Reset form
      setEnquiryData({
        name: '',
        email: '',
        phone: '',
        location: '',
        service: 'solar',
        message: ''
      });

      // Show success
      alert(`Thank you, ${enquiryData.name}! Your enquiry has been submitted. Enquiry ID: ${response.data.enquiry_id}`);
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      alert("Sorry, there was an error submitting your enquiry. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  }, [enquiryData, chat]);

  // Open enquiry form with context from chat
  const openEnquiryFormWithContext = useCallback(() => {
    const detectedService = chat.detectServiceFromChat();
    setEnquiryData(prev => ({ ...prev, service: detectedService }));
    setShowEnquiryForm(true);
  }, [chat]);

  // Admin page - full screen without header/footer
  if (currentPage === 'admin') {
    return (
      <Suspense fallback={<PageLoader />}>
        <AdminPage onBack={() => handleSetCurrentPage('home')} />
      </Suspense>
    );
  }

  // Check if we're on a snap-scroll page (home, about, contact have full-viewport sections)
  // Pages that have their own integrated footer (don't add global footer)
  const hasIntegratedFooter = [
    'home', 'about', 'contact', 'solar', 'wind', 'marine', 'public-infra',
    'terms', 'privacy', 'accessibility', 'insights'
  ].includes(currentPage);
  
  // Snap scroll pages (for scroll progress bar and overflow handling)
  const isSnapScrollPage = currentPage === 'home' || currentPage === 'about' || currentPage === 'contact';

  return (
    <>
      {/* Loading Screen */}
      <LoadingScreen isLoading={isLoading} />
      
      {/* Scroll Progress Bar */}
      {!isLoading && isSnapScrollPage && <ScrollProgressBar progress={scrollProgress} />}
      
      <div className={`
        ${isSnapScrollPage ? 'h-screen overflow-hidden' : 'min-h-screen'} 
        bg-gradient-to-br from-emerald-50 via-white to-lime-50
        transition-opacity duration-300
        ${isPageTransitioning ? 'opacity-0' : 'opacity-100'}
        ${isLoading ? 'opacity-0' : 'opacity-100'}
      `}>
      {/* Skip Link for Accessibility - WCAG 2.4.1 */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-emerald-900 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
        aria-label="Skip to main content"
      >
        Skip to main content
      </a>
      
      {/* Screen Reader Route Announcements - WCAG 4.1.3 Status Messages */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        role="status"
        className="sr-only"
      >
        {pageAnnouncement}
      </div>
      
      {/* Navigation - fixed overlay on snap-scroll pages, sticky on other pages */}
      <Header 
        currentPage={currentPage}
        setCurrentPage={handleSetCurrentPage}
        onOpenChat={() => chat.setIsOpen(true)}
        isOverlay={isSnapScrollPage}
      />

      {/* Page Content with Suspense */}
      <Suspense fallback={<PageLoader />}>
        <main 
          id="main-content" 
          ref={mainContentRef}
          tabIndex={-1}
          aria-label="Main content"
          className="outline-none"
        >
        {currentPage === 'home' && (
          <HomePage 
            onTopicClick={chat.handleTopicClick} 
            scrollToSection={pendingScrollTarget}
            onScrollComplete={() => setPendingScrollTarget(null)}
          />
        )}

        {currentPage === 'about' && (
          <AboutPage 
            scrollToSection={pendingScrollTarget} 
            onScrollComplete={() => setPendingScrollTarget(null)} 
          />
        )}

        {currentPage === 'contact' && (
          <ContactPage
            enquiryData={enquiryData}
            setEnquiryData={setEnquiryData}
            onSubmit={handleEnquirySubmit}
            isLoading={isSubmitting}
          />
        )}

        {currentPage === 'insights' && <InsightsPage />}

        {currentPage === 'solar' && <SolarPage />}
        
        {currentPage === 'wind' && <WindPage />}
        
        {currentPage === 'marine' && <MarinePage />}
        
        {currentPage === 'public-infra' && <PublicInfraPage />}

        {currentPage === 'privacy' && <PrivacyPolicyPage />}
        
        {currentPage === 'terms' && <TermsPage />}
        
        {currentPage === 'accessibility' && <AccessibilityPage />}
        </main>
      </Suspense>

      {/* Footer - only shown on pages without integrated footer */}
      {!hasIntegratedFooter && <Footer setCurrentPage={handleSetCurrentPage} />}

      {/* Enquiry Form Modal - Lazy loaded */}
      <Suspense fallback={null}>
        <EnquiryFormModal
          isOpen={showEnquiryForm}
          onClose={() => setShowEnquiryForm(false)}
          enquiryData={enquiryData}
          setEnquiryData={setEnquiryData}
          onSubmit={handleEnquirySubmit}
          isLoading={isSubmitting}
        />
      </Suspense>

      {/* Chat Widget - Lazy loaded */}
      <Suspense fallback={null}>
        <ChatWidget
          isOpen={chat.isOpen}
          setIsOpen={chat.setIsOpen}
          messages={chat.messages}
          setMessages={chat.setMessages}
          inputMessage={chat.inputMessage}
          setInputMessage={chat.setInputMessage}
          isLoading={chat.isLoading}
          isRecording={chat.isRecording}
          selectedFile={chat.selectedFile}
          setSelectedFile={chat.setSelectedFile}
          selectedLanguage={chat.selectedLanguage}
          setSelectedLanguage={chat.setSelectedLanguage}
          respondInSameLanguage={chat.respondInSameLanguage}
          setRespondInSameLanguage={chat.setRespondInSameLanguage}
          unreadCount={chat.unreadCount}
          onSendMessage={chat.sendMessage}
          onStartRecording={chat.startRecording}
          onStopRecording={chat.stopRecording}
          onFileSelect={chat.setSelectedFile}
          onFileUpload={chat.handleFileUpload}
          onOpenEnquiryForm={openEnquiryFormWithContext}
        />
      </Suspense>
      
      {/* Accessibility Toolbar - Bottom Left */}
      <AccessibilityToolbar />
      
      {/* Cookie Consent Banner */}
      <CookieConsentBanner />
    </div>
    </>
  );
}

export default App;
